import React from "react";
import PropTypes from "prop-types";
import { getIn } from "formik";
import { Add } from "@mui/icons-material";
import EmptyRolesState from "./EmptyRolesState";
import RoleEditor from "./RoleEditor";
import RolesNavigation from "./RolesNavigation";
import { UIButton } from "../../../../ui";
import { firstTruthyBoundaryMeterString } from "./helpers";
import { hasNestedError } from "./defineRolesAndShiftsUtils";

const DefineRolesAndShiftsFormBody = ({
  formik,
  roles,
  rolesOptions,
  activeRoleIndex,
  activeRole,
  activeShiftIndex,
  invalidRoleIndexes,
  validationAttempt,
  activeRoleValidated,
  emptyRolesAttention,
  emptyRolesRef,
  isRoleAccordionExpanded,
  onSelectRole,
  onAddRole,
  onAccordionExpandedChange,
  onRemoveRole,
  onShiftChange,
  onAddShift,
  onRemoveShift,
  onTrainingToggle,
  eventLocationBoundaryMeters,
  eventRadius,
  removeRoleDisabled,
  portalCompanyOngoingEdit,
  companyPortalMode,
  isEditMode,
  addAnotherRoleLabel,
  capacity,
}) => (
  <div className="define-roles-step__layout">
    <RolesNavigation
      crewCapacity={capacity}
      roles={roles}
      rolesOptions={rolesOptions}
      activeRoleIndex={activeRoleIndex}
      invalidRoleIndexes={invalidRoleIndexes}
      validationAttempt={validationAttempt}
      hasEmptyRolesError={emptyRolesAttention > 0 && !roles.length}
      onSelectRole={onSelectRole}
      onAddRole={onAddRole}
    />

    <section className="define-roles-step__content">
      {!roles.length ? (
        <div ref={emptyRolesRef}>
          <EmptyRolesState
            key={emptyRolesAttention}
            attentionKey={emptyRolesAttention}
            hasValidationError={emptyRolesAttention > 0}
            onAddRole={onAddRole}
          />
        </div>
      ) : (
        <>
          <RoleEditor
            formik={formik}
            role={activeRole}
            roleIndex={activeRoleIndex}
            rolesOptions={rolesOptions}
            activeShiftIndex={activeShiftIndex}
            accordionExpanded={isRoleAccordionExpanded}
            onAccordionExpandedChange={onAccordionExpandedChange}
            onRemoveRole={onRemoveRole}
            onShiftChange={onShiftChange}
            onAddShift={onAddShift}
            onRemoveShift={onRemoveShift}
            onTrainingToggle={onTrainingToggle}
            defaultTrainingBoundaryMeters={firstTruthyBoundaryMeterString(
              eventLocationBoundaryMeters,
              eventRadius
            )}
            removeRoleDisabled={removeRoleDisabled}
            capacityCeiling={
              capacity?.onGoToCrewSize || capacity?.crewSizeLocked
                ? capacity.ceiling
                : null
            }
            portalCompanyOngoingEdit={portalCompanyOngoingEdit}
            companyPortalMode={companyPortalMode}
            isEditMode={isEditMode}
            hasError={
              validationAttempt > 0 &&
              activeRoleValidated &&
              hasNestedError(
                getIn(
                  formik.errors,
                  `event_roles_attributes.${activeRoleIndex}`
                )
              )
            }
          />
          <UIButton
            className="add-more"
            onClick={onAddRole}
            variant="text"
            startIcon={<Add />}
            type="button"
          >
            {addAnotherRoleLabel}
          </UIButton>
        </>
      )}
    </section>
  </div>
);

DefineRolesAndShiftsFormBody.propTypes = {
  formik: PropTypes.object.isRequired,
  roles: PropTypes.array.isRequired,
  rolesOptions: PropTypes.arrayOf(PropTypes.object),
  activeRoleIndex: PropTypes.number.isRequired,
  activeRole: PropTypes.object,
  activeShiftIndex: PropTypes.number.isRequired,
  invalidRoleIndexes: PropTypes.arrayOf(PropTypes.number).isRequired,
  validationAttempt: PropTypes.number.isRequired,
  activeRoleValidated: PropTypes.bool.isRequired,
  emptyRolesAttention: PropTypes.number.isRequired,
  emptyRolesRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  isRoleAccordionExpanded: PropTypes.bool.isRequired,
  onSelectRole: PropTypes.func.isRequired,
  onAddRole: PropTypes.func.isRequired,
  onAccordionExpandedChange: PropTypes.func.isRequired,
  onRemoveRole: PropTypes.func.isRequired,
  onShiftChange: PropTypes.func.isRequired,
  onAddShift: PropTypes.func.isRequired,
  onRemoveShift: PropTypes.func.isRequired,
  onTrainingToggle: PropTypes.func.isRequired,
  eventLocationBoundaryMeters: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  eventRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  removeRoleDisabled: PropTypes.bool,
  portalCompanyOngoingEdit: PropTypes.bool,
  companyPortalMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
  addAnotherRoleLabel: PropTypes.string.isRequired,
  /** Crew size capacity (company create only); null hides the counter. */
  capacity: PropTypes.shape({
    used: PropTypes.number,
    ceiling: PropTypes.number,
    crewSizeLabel: PropTypes.string,
    onGoToCrewSize: PropTypes.func,
    crewSizeLocked: PropTypes.bool,
  }),
};

DefineRolesAndShiftsFormBody.defaultProps = {
  rolesOptions: [],
  activeRole: undefined,
  emptyRolesRef: null,
  eventLocationBoundaryMeters: "",
  eventRadius: undefined,
  removeRoleDisabled: false,
  portalCompanyOngoingEdit: false,
  companyPortalMode: false,
  isEditMode: false,
  capacity: null,
};

export default DefineRolesAndShiftsFormBody;
